import { Component, EventEmitter, HostBinding, Input, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { AirtableService } from 'src/app/services/airtable.service';
import { AlarmoState, Apartment } from 'src/graphql/generated/schema';

@Component({
  selector: 'welcome-panel',
  templateUrl: './welcome-panel.component.html',
  styleUrls: ['./welcome-panel.component.scss']
})
export class WelcomePanelComponent implements OnInit, OnDestroy {
  @Input() alarmoStatusChangeEvent!: EventEmitter<AlarmoState>;
  public welcomeTitle = 'Welcome home';
  public leaveTitle = 'See you soon';

  public username: string = '';
  @HostBinding('attr.leave')
  public inUserLeave: boolean = false;

  // Long press functionality
  private longPressTimer: any;
  private readonly LONG_PRESS_DURATION = 3000; // 3 seconds
  private destroy$ = new Subject<void>();
  private retryAttempts = 0;
  private readonly MAX_RETRY_ATTEMPTS = 10;
  private readonly RETRY_DELAY_MS = 500;

  constructor(
    private authService: AuthService,
    private airtableService: AirtableService
  ) {}

  public ngOnInit(): void {
    this.alarmoStatusChangeEvent.subscribe((status) => {
      this.inUserLeave = status === AlarmoState.Armed;
    })

    // Load cached username immediately for instant display
    this.loadCachedUsername();

    // Wait for user to be fully authenticated before trying to get fresh attributes
    this.authService.getFullyAuthenticated().pipe(
      filter(isAuthenticated => isAuthenticated === true),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadUsername();
    });

    // Also try immediately in case user is already authenticated
    if (this.authService.isFullyAuthenticated()) {
      this.loadUsername();
    }
  }

  private loadCachedUsername(): void {
    try {
      const cachedUsername = localStorage.getItem('cached_username');
      if (cachedUsername) {
        this.username = cachedUsername;
        // console.log('[WelcomePanel] Loaded cached username');
      }
    } catch (e) {
      console.warn('[WelcomePanel] Failed to load cached username:', e);
    }
  }

  private loadUsername(): void {
    // Check if cognitoUser is available
    if (!this.authService.cognitoUser) {
      // Retry if not available yet
      if (this.retryAttempts < this.MAX_RETRY_ATTEMPTS) {
        this.retryAttempts++;
        timer(this.RETRY_DELAY_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.loadUsername();
        });
      }
      return;
    }

    // First verify that session is actually valid before calling getUserAttributes
    this.authService.cognitoUser.getSession((sessionErr: any, session: any) => {
      if (sessionErr || !session?.isValid()) {
        // Session not ready yet - retry silently
        if (this.retryAttempts < this.MAX_RETRY_ATTEMPTS) {
          this.retryAttempts++;
          timer(this.RETRY_DELAY_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadUsername();
          });
        }
        return;
      }

      // Session is valid, now get user attributes
      this.authService.getUserAttributes(
        (err: any) => {
          // Only log real errors, not session-not-ready errors
          const errorMsg = err?.message || err?.code || '';
          if (errorMsg && !errorMsg.includes('missing') && !errorMsg.includes('Token')) {
            console.warn('[WelcomePanel] Error getting user attributes:', errorMsg);
          }
          // Retry if error occurred and we haven't exceeded max attempts
          if (this.retryAttempts < this.MAX_RETRY_ATTEMPTS) {
            this.retryAttempts++;
            timer(this.RETRY_DELAY_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
              this.loadUsername();
            });
          }
        },
        (res: any) => {
          this.username = res?.find((el: { Name: string; }) => el.Name == 'given_name')?.Value || '';
          if (this.username) {
            // Cache username for faster subsequent loads
            try {
              localStorage.setItem('cached_username', this.username);
            } catch (e) {
              // Ignore localStorage errors
            }
          }
        });
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Start long press timer when user starts pressing
   */
  public onPressStart(): void {
    this.longPressTimer = setTimeout(() => {
      this.onLongPress();
    }, this.LONG_PRESS_DURATION);
  }

  /**
   * Cancel long press timer when user releases or moves away
   */
  public onPressEnd(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /**
   * Handle long press completion - could be used to show additional dashboard data
   */
  private async onLongPress(): Promise<void> {
    console.log('Long press detected on welcome message');
    
    try {
      // You could use this to show special content or load additional data from Airtable
      // For example: const specialData = await this.airtableService.getDashboardData();
      
      console.log('Long press handled successfully');
    } catch (error) {
      console.error('Error handling long press:', error);
    }
    
    // Clear the timer
    this.longPressTimer = null;
  }
}
